import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Modal,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateRangePicker } from 'react-date-range';
import { format } from 'date-fns';
import dayjs from 'dayjs';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { download, generateCsv, mkConfig } from 'export-to-csv';
import { useSelector } from 'react-redux';
import BreadCrumb from '../../../components/breadcrumb/BreadCrumbNavigation';
import TableWrapper from '../../../common/TableWrapper';
import EmptyContent from '../../../components/empty-content';
import TablePaginationCustom from '../../../components/table/table-pagination-custom';
import { getUserRoles } from '../../../services/employeeService';
import { getAllCompanies } from '../../../services/leaveService';
import { getSurveyResponses } from '../../../services/surveyService';
import { isSuperAdmin } from '../../../utils/share';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

const DEFAULT_QUERY = {
  pageNumber: 1,
  pageSize: 100,
  search: '',
  periodKey: '',
  userRoleId: '',
  companyId: '',
  surveyDateFrom: '',
  surveyDateTo: '',
  orderBy: 'submittedAt',
  order: 'DESC',
};

const getDefaultQuery = (enforcedCompanyId = '') => ({
  ...DEFAULT_QUERY,
  companyId: enforcedCompanyId,
});

const SORT_OPTIONS = [
  { value: 'submittedAt', label: 'Submitted at' },
  { value: 'surveyDate', label: 'Survey date' },
];

const ORDER_OPTIONS = [
  { value: 'DESC', label: 'Newest first' },
  { value: 'ASC', label: 'Oldest first' },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const BACKEND_SORTABLE_COLUMNS = ['submittedAt', 'surveyDate'];
const SUBMISSION_STATUS_OPTIONS = [
  { value: 'filled', label: 'Filled' },
  { value: 'not_filled', label: 'Not Filled' },
];

const csvConfig = mkConfig({
  fieldSeparator: ',',
  decimalSeparator: '.',
  useKeysAsHeaders: true,
  filename: 'survey-responses-current-page',
});

const parsePositiveNumber = (value, fallback) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const normalizeAnswerValue = (answer) => {
  if (answer?.answerText?.trim()) {
    return {
      type: 'text',
      value: answer.answerText.trim(),
    };
  }

  if (answer?.answerJson) {
    return {
      type: 'json',
      value: typeof answer.answerJson === 'string' ? answer.answerJson : JSON.stringify(answer.answerJson, null, 2),
    };
  }

  return {
    type: 'empty',
    value: 'No response',
  };
};

const sortAnswers = (answers = []) =>
  [...answers].sort((firstAnswer, secondAnswer) => {
    const firstOrder = firstAnswer?.question?.sortOrder ?? Number.MAX_SAFE_INTEGER;
    const secondOrder = secondAnswer?.question?.sortOrder ?? Number.MAX_SAFE_INTEGER;
    return firstOrder - secondOrder;
  });

const formatDateLabel = (value) => (value ? format(new Date(value), 'dd MMM yyyy') : 'N/A');

const formatDateTimeLabel = (value) => (value ? format(new Date(value), 'dd MMM yy, hh:mm a') : 'N/A');
const formatDateRangeLabel = (startDate, endDate) => {
  if (!startDate && !endDate) {
    return 'Select survey date range';
  }

  const startLabel = startDate ? formatDateLabel(startDate) : 'Start date';
  const endLabel = endDate ? formatDateLabel(endDate) : 'End date';

  return `${startLabel} - ${endLabel}`;
};

const getAnswerPreview = (response, questionId) => {
  if (response?.submissionStatus === 'not_filled') {
    return 'N/A';
  }

  const answer = response?.answers?.find((item) => (item?.question?.id ?? item?.questionId) === questionId);
  return normalizeAnswerValue(answer).value;
};

const escapeRegExp = (value = '') => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const highlightText = (text, query) => {
  const content = text ?? '';

  if (!content || !query?.trim()) {
    return content || 'N/A';
  }

  const matcher = new RegExp(`(${escapeRegExp(query.trim())})`, 'ig');
  const parts = String(content).split(matcher);
  const normalizedQuery = query.trim().toLowerCase();

  return parts.map((part, index) =>
    part.toLowerCase() === normalizedQuery ? (
      <Box
        key={`${part}-${index}`}
        component="mark"
        sx={{
          px: 0.25,
          borderRadius: 0.5,
          backgroundColor: '#FFF3BF',
          color: 'inherit',
        }}
      >
        {part}
      </Box>
    ) : (
      <Box key={`${part}-${index}`} component="span">
        {part}
      </Box>
    )
  );
};

const buildInitialQuery = (searchParams, canFilterCompany, enforcedCompanyId = '') => {
  const defaultQuery = getDefaultQuery(enforcedCompanyId);
  const orderBy = SORT_OPTIONS.find((option) => option.value === searchParams.get('orderBy'))?.value ?? DEFAULT_QUERY.orderBy;
  const order = ORDER_OPTIONS.find((option) => option.value === searchParams.get('order'))?.value ?? DEFAULT_QUERY.order;
  const pageSize = PAGE_SIZE_OPTIONS.includes(parsePositiveNumber(searchParams.get('pageSize'), DEFAULT_QUERY.pageSize))
    ? parsePositiveNumber(searchParams.get('pageSize'), DEFAULT_QUERY.pageSize)
    : DEFAULT_QUERY.pageSize;

  return {
    pageNumber: parsePositiveNumber(searchParams.get('pageNumber'), defaultQuery.pageNumber),
    pageSize,
    search: searchParams.get('search') ?? defaultQuery.search,
    periodKey: searchParams.get('periodKey') ?? defaultQuery.periodKey,
    userRoleId: searchParams.get('userRoleId') ?? defaultQuery.userRoleId,
    companyId: canFilterCompany ? searchParams.get('companyId') ?? defaultQuery.companyId : enforcedCompanyId,
    surveyDateFrom: searchParams.get('surveyDateFrom') ?? defaultQuery.surveyDateFrom,
    surveyDateTo: searchParams.get('surveyDateTo') ?? defaultQuery.surveyDateTo,
    orderBy,
    order,
  };
};

function SurveyFilters({
  searchInput,
  query,
  activeSubmissionStatus,
  filledCount,
  notFilledCount,
  onSubmissionStatusChange,
  onSearchChange,
  onFilterChange,
  onResetFilters,
  roleOptions,
  companyOptions,
  canFilterCompany,
  activeFilterCount,
  onExportCurrentPage,
  exportDisabled,
}) {
  const hasActiveFilters = activeFilterCount > 0;
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateRange, setDateRange] = useState({
    selection: {
      startDate: query.surveyDateFrom ? new Date(query.surveyDateFrom) : new Date(),
      endDate: query.surveyDateTo ? new Date(query.surveyDateTo) : query.surveyDateFrom ? new Date(query.surveyDateFrom) : new Date(),
      key: 'selection',
    },
  });

  useEffect(() => {
    setDateRange({
      selection: {
        startDate: query.surveyDateFrom ? new Date(query.surveyDateFrom) : new Date(),
        endDate: query.surveyDateTo ? new Date(query.surveyDateTo) : query.surveyDateFrom ? new Date(query.surveyDateFrom) : new Date(),
        key: 'selection',
      },
    });
  }, [query.surveyDateFrom, query.surveyDateTo]);

  const openDateRangePicker = () => setShowDatePicker(true);
  const closeDateRangePicker = () => setShowDatePicker(false);

  const handleDateRangeChange = (ranges) => {
    setDateRange((previousRange) => ({
      ...previousRange,
      ...ranges,
    }));
  };

  const handleApplyDateRange = () => {
    const startDate = dateRange?.selection?.startDate ? dayjs(dateRange.selection.startDate).format('YYYY-MM-DD') : '';
    const endDate = dateRange?.selection?.endDate ? dayjs(dateRange.selection.endDate).format('YYYY-MM-DD') : '';

    onFilterChange('surveyDateFrom', startDate);
    onFilterChange('surveyDateTo', endDate);
    closeDateRangePicker();
  };

  const selectedDateRangeText = formatDateRangeLabel(query.surveyDateFrom, query.surveyDateTo);

  return (
    <Card
      sx={{
        mb: 3,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        background: 'linear-gradient(135deg, rgba(255,249,235,0.95) 0%, rgba(242,248,255,0.95) 100%)',
      }}
    >
      <CardContent sx={{ p: { xs: 2, md: 3 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={1.5}
          sx={{ mb: 2.5 }}
        >
          <Box>
            <Typography variant="h4" sx={{ mb: 0.5 }}>
              Survey Responses
            </Typography>
            {/* <Typography variant="body2" color="text.secondary">
              Search submissions, switch between filled and not-filled survey records, and review the results quickly.
            </Typography> */}
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label={`${activeFilterCount} active filter${activeFilterCount === 1 ? '' : 's'}`} color="default" />
            <Tooltip title="Exports the submissions currently loaded on this page">
              <span>
                <Button
                  variant="outlined"
                  startIcon={<FileDownloadOutlinedIcon />}
                  onClick={onExportCurrentPage}
                  disabled={exportDisabled}
                >
                  Export page
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2.5 }}>
          {SUBMISSION_STATUS_OPTIONS.map((option) => {
            const count = option.value === 'filled' ? filledCount : notFilledCount;
            const isActive = activeSubmissionStatus === option.value;

            return (
              <Chip
                key={option.value}
                label={`${option.label} (${count})`}
                color={isActive ? 'primary' : 'default'}
                variant={isActive ? 'filled' : 'outlined'}
                onClick={() => onSubmissionStatusChange(option.value)}
              />
            );
          })}
        </Stack>

        <Stack spacing={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Search submissions"
                placeholder="Name, email, answers, questions..."
                value={searchInput}
                onChange={(event) => onSearchChange(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <SearchRoundedIcon
                      sx={{
                        mr: 1,
                        color: 'text.secondary',
                      }}
                    />
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Year-Week"
                placeholder="2026-14"
                value={query.periodKey}
                onChange={(event) => onFilterChange('periodKey', event.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Role"
                value={query.userRoleId}
                onChange={(event) => onFilterChange('userRoleId', event.target.value)}
              >
                <MenuItem value="">All roles</MenuItem>
                {roleOptions.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.roleName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {canFilterCompany ? (
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Company"
                  value={query.companyId}
                  onChange={(event) => onFilterChange('companyId', event.target.value)}
                >
                  <MenuItem value="">All companies</MenuItem>
                  {companyOptions.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            ) : (
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Survey date range"
                  value={selectedDateRangeText}
                  onClick={openDateRangePicker}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <CalendarMonthOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                    sx: {
                      cursor: 'pointer',
                    },
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      cursor: 'pointer',
                    },
                  }}
                />
              </Grid>
            )}
          </Grid>

          <Grid container spacing={2} alignItems="stretch">
            {canFilterCompany && (
              <Grid item xs={12} sm={6} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Survey date range"
                  value={selectedDateRangeText}
                  onClick={openDateRangePicker}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <CalendarMonthOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                    sx: {
                      cursor: 'pointer',
                    },
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      cursor: 'pointer',
                    },
                  }}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6} md={canFilterCompany ? 2 : 3}>
              <TextField
                select
                fullWidth
                size="small"
                label="Sort by"
                value={query.orderBy}
                onChange={(event) => onFilterChange('orderBy', event.target.value)}
              >
                {SORT_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={canFilterCompany ? 2 : 3}>
              <TextField
                select
                fullWidth
                size="small"
                label="Order"
                value={query.order}
                onChange={(event) => onFilterChange('order', event.target.value)}
              >
                {ORDER_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={canFilterCompany ? 2 : 2} sx={{ display: 'flex' }}>
              <Button
                fullWidth
                color="inherit"
                variant={hasActiveFilters ? 'outlined' : 'text'}
                onClick={onResetFilters}
                disabled={!hasActiveFilters}
                sx={{ minHeight: 40 }}
              >
                Clear filters
              </Button>
            </Grid>
          </Grid>
        </Stack>
      </CardContent>

      <Modal open={showDatePicker} onClose={closeDateRangePicker}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '94%', sm: '648px' },
            bgcolor: 'background.paper',
            borderRadius: '10px',
            boxShadow: 24,
            p: { xs: 2, sm: 4 },
            '& .rdrDateDisplayWrapper': {
              justifyContent: 'center',
            },
          }}
        >
          <DateRangePicker
            onChange={handleDateRangeChange}
            months={1}
            direction="vertical"
            scroll={{ enabled: true }}
            ranges={[dateRange.selection]}
            maxDate={new Date()}
          />
          <Button variant="contained" sx={{ marginTop: '-40px' }} onClick={handleApplyDateRange}>
            Apply
          </Button>
        </Box>
      </Modal>
    </Card>
  );
}

SurveyFilters.propTypes = {
  activeSubmissionStatus: PropTypes.oneOf(['filled', 'not_filled']).isRequired,
  activeFilterCount: PropTypes.number.isRequired,
  canFilterCompany: PropTypes.bool.isRequired,
  companyOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  exportDisabled: PropTypes.bool.isRequired,
  filledCount: PropTypes.number.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onExportCurrentPage: PropTypes.func.isRequired,
  onResetFilters: PropTypes.func.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onSubmissionStatusChange: PropTypes.func.isRequired,
  notFilledCount: PropTypes.number.isRequired,
  query: PropTypes.object.isRequired,
  roleOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  searchInput: PropTypes.string.isRequired,
};

function SurveyResponses() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = useSelector((state) => state?.login?.user || null);
  const isSuperAdminUser = isSuperAdmin();
  const enforcedCompanyId = !isSuperAdminUser && currentUser?.companyId ? String(currentUser.companyId) : '';
  const canFilterCompany = isSuperAdminUser;
  const [query, setQuery] = useState(() => buildInitialQuery(searchParams, canFilterCompany, enforcedCompanyId));
  const [searchInput, setSearchInput] = useState(
    () => buildInitialQuery(searchParams, canFilterCompany, enforcedCompanyId).search
  );
  const [responses, setResponses] = useState([]);
  const [notFilledResponses, setNotFilledResponses] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [notFilledTotalCount, setNotFilledTotalCount] = useState(0);
  const [roleOptions, setRoleOptions] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [activeSubmissionStatus, setActiveSubmissionStatus] = useState('filled');
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);
  const [isMetaLoading, setIsMetaLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const breadcrumbs = [
    { label: 'Home', path: '/' },
    { label: 'Admin', path: '/admin/developer-scorecard' },
    { label: 'Survey Responses', path: '/admin/survey-responses' },
  ];

  const activeFilterCount = useMemo(
    () =>
      [
        query.search,
        query.periodKey,
        query.userRoleId,
        canFilterCompany ? query.companyId : '',
        query.surveyDateFrom,
        query.surveyDateTo,
      ].filter(Boolean).length,
    [canFilterCompany, query.companyId, query.periodKey, query.search, query.surveyDateFrom, query.surveyDateTo, query.userRoleId]
  );

  const visibleRows = activeSubmissionStatus === 'filled' ? responses : notFilledResponses;
  const visibleCount = activeSubmissionStatus === 'filled' ? totalCount : notFilledTotalCount;
  const totalPages = Math.max(1, Math.ceil(visibleCount / query.pageSize));
  const pageStart = visibleCount ? (query.pageNumber - 1) * query.pageSize + 1 : 0;
  const pageEnd = visibleCount ? Math.min(query.pageNumber * query.pageSize, visibleCount) : 0;
  const sortingState = useMemo(
    () =>
      BACKEND_SORTABLE_COLUMNS.includes(query.orderBy)
        ? [
            {
              id: query.orderBy,
              desc: query.order === 'DESC',
            },
          ]
        : [],
    [query.order, query.orderBy]
  );
  const questionColumns = useMemo(() => {
    const uniqueQuestions = [];
    const seenQuestionIds = new Set();

    responses.forEach((response) => {
      sortAnswers(response?.answers).forEach((answer) => {
        const questionId = answer?.question?.id ?? answer?.questionId;

        if (!questionId || seenQuestionIds.has(questionId)) {
          return;
        }

        seenQuestionIds.add(questionId);
        uniqueQuestions.push({
          id: questionId,
          questionText: answer?.question?.questionText || `Question ${uniqueQuestions.length + 1}`,
          sortOrder: answer?.question?.sortOrder ?? Number.MAX_SAFE_INTEGER,
        });
      });
    });

    return uniqueQuestions
      .sort((firstQuestion, secondQuestion) => firstQuestion.sortOrder - secondQuestion.sortOrder)
      .slice(0, 2);
  }, [responses]);

  const updateQuery = useCallback((updater) => {
    setQuery((previousQuery) => {
      const nextQuery = typeof updater === 'function' ? updater(previousQuery) : updater;
      return nextQuery;
    });
  }, []);

  const filledColumns = useMemo(
    () => [
      {
        id: 'employee',
        header: 'Employee',
        size: 220,
        enableSorting: false,
        Cell: ({ row }) => (
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {highlightText(row.original?.user?.userName || 'Unknown employee', query.search)}
          </Typography>
        ),
      },
      {
        accessorKey: 'submittedAt',
        header: 'Submitted At',
        Cell: ({ row }) => (row.original?.submissionStatus === 'not_filled' ? 'N/A' : formatDateTimeLabel(row.original?.submittedAt)),
        size: 220,
      },
      {
        accessorKey: 'surveyDate',
        header: 'Survey Date',
        Cell: ({ row }) => formatDateLabel(row.original?.surveyDate),
        size: 140,
      },
      {
        id: `question-${questionColumns[0]?.id || '1'}`,
        header: questionColumns[0]?.questionText || 'Question 1',
        accessorFn: (row) => (questionColumns[0] ? getAnswerPreview(row, questionColumns[0].id) : 'No response'),
        size: 460,
        enableSorting: false,
        Cell: ({ cell }) => (
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
            }}
          >
            {highlightText(cell.getValue(), query.search)}
          </Typography>
        ),
      },
      {
        id: `question-${questionColumns[1]?.id || '2'}`,
        header: questionColumns[1]?.questionText || 'Question 2',
        accessorFn: (row) => (questionColumns[1] ? getAnswerPreview(row, questionColumns[1].id) : 'No response'),
        size: 460,
        enableSorting: false,
        Cell: ({ cell }) => (
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
            }}
          >
            {highlightText(cell.getValue(), query.search)}
          </Typography>
        ),
      },
    ],
    [query.search, questionColumns]
  );
  const columns = filledColumns;

  const handleTableSortingChange = useCallback(
    (updater) => {
      const nextSorting = typeof updater === 'function' ? updater(sortingState) : updater;
      const nextSort = nextSorting?.[0];

      if (!nextSort || !BACKEND_SORTABLE_COLUMNS.includes(nextSort.id)) {
        updateQuery((previousQuery) => ({
          ...previousQuery,
          orderBy: DEFAULT_QUERY.orderBy,
          order: DEFAULT_QUERY.order,
          pageNumber: 1,
        }));
        return;
      }

      updateQuery((previousQuery) => ({
        ...previousQuery,
        orderBy: nextSort.id,
        order: nextSort.desc ? 'DESC' : 'ASC',
        pageNumber: 1,
      }));
    },
    [sortingState, updateQuery]
  );

  const table = useMaterialReactTable({
    columns,
    data: visibleRows,
    manualSorting: true,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableSorting: true,
    enableSortingRemoval: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enablePagination: false,
    enableStickyHeader: true,
    onSortingChange: handleTableSortingChange,
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        border: 'none',
        boxShadow: 'none',
      },
    },
    muiTableContainerProps: {
      sx: {
        maxHeight: 620,
      },
    },
    muiToolbarAlertBannerProps: errorMessage ? { color: 'error', children: errorMessage } : undefined,
    state: {
      isLoading: isInitialLoading,
      sorting: sortingState,
      showProgressBars: isRefetching || isMetaLoading,
      showAlertBanner: Boolean(errorMessage),
      showSkeletons: isInitialLoading,
    },
  });

  const fetchMeta = useCallback(async () => {
    setIsMetaLoading(true);

    try {
      const requests = [getUserRoles()];

      if (canFilterCompany) {
        requests.push(getAllCompanies());
      }

      const [rolesResponse, companiesResponse] = await Promise.all(requests);
      setRoleOptions(rolesResponse?.data?.data || []);
      setCompanyOptions(companiesResponse?.data?.data || []);
    } catch (error) {
      toast.error('Unable to load some filter options right now.');
    } finally {
      setIsMetaLoading(false);
    }
  }, [canFilterCompany]);

  const fetchResponseList = useCallback(
    async (currentQuery) => {
      const shouldShowInitialLoader = !responses.length && currentQuery.pageNumber === 1;

      setErrorMessage('');
      if (shouldShowInitialLoader) {
        setIsInitialLoading(true);
      } else {
        setIsRefetching(true);
      }

      try {
        const response = await getSurveyResponses(currentQuery);
        const payload = response?.data?.data || {};
        const nextRows = (payload.rows || []).map((item) => ({
          ...item,
          answers: sortAnswers(item.answers),
        }));
        const nextNotFilledRows = (payload.notFilledRows || []).map((item) => ({
          ...item,
          answers: [],
        }));

        setResponses(nextRows);
        setTotalCount(payload.count || 0);
        setNotFilledResponses(nextNotFilledRows);
        setNotFilledTotalCount(payload.notFilledCount || 0);

        const nextVisibleCount = activeSubmissionStatus === 'filled' ? payload.count || 0 : payload.notFilledCount || 0;
        const nextVisibleRows = activeSubmissionStatus === 'filled' ? nextRows : nextNotFilledRows;

        if (currentQuery.pageNumber > 1 && nextVisibleRows.length === 0 && nextVisibleCount > 0) {
          updateQuery((previousQuery) => ({
            ...previousQuery,
            pageNumber: Math.max(1, Math.ceil(nextVisibleCount / previousQuery.pageSize)),
          }));
        }
      } catch (error) {
        setErrorMessage(error?.response?.data?.message || 'Unable to fetch survey responses right now.');
        setResponses([]);
        setTotalCount(0);
        setNotFilledResponses([]);
        setNotFilledTotalCount(0);
      } finally {
        setIsInitialLoading(false);
        setIsRefetching(false);
      }
    },
    [activeSubmissionStatus, responses.length, updateQuery]
  );

  const syncQueryParams = useCallback(
    (currentQuery) => {
      const nextParams = new URLSearchParams();

      Object.entries(currentQuery).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (
            (key === 'pageNumber' && value === DEFAULT_QUERY.pageNumber) ||
            (key === 'pageSize' && value === DEFAULT_QUERY.pageSize) ||
            (key === 'orderBy' && value === DEFAULT_QUERY.orderBy) ||
            (key === 'order' && value === DEFAULT_QUERY.order) ||
            (key === 'companyId' && !canFilterCompany)
          ) {
            return;
          }

          nextParams.set(key, String(value));
        }
      });

      setSearchParams(nextParams, { replace: true });
    },
    [canFilterCompany, setSearchParams]
  );

  useEffect(() => {
    fetchMeta();
  }, [fetchMeta]);

  useEffect(() => {
    if (!canFilterCompany && enforcedCompanyId && query.companyId !== enforcedCompanyId) {
      setQuery((previousQuery) => ({
        ...previousQuery,
        companyId: enforcedCompanyId,
        pageNumber: 1,
      }));
    }
  }, [canFilterCompany, enforcedCompanyId, query.companyId]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      updateQuery((previousQuery) => {
        if (previousQuery.search === searchInput.trim()) {
          return previousQuery;
        }

        return {
          ...previousQuery,
          search: searchInput.trim(),
          pageNumber: 1,
        };
      });
    }, 400);

    return () => clearTimeout(debounceTimer);
  }, [searchInput, updateQuery]);

  useEffect(() => {
    syncQueryParams(query);
    fetchResponseList(query);
  }, [fetchResponseList, query, syncQueryParams]);

  const handleFilterChange = (field, value) => {
    updateQuery((previousQuery) => ({
      ...previousQuery,
      [field]: value,
      pageNumber: field === 'pageNumber' ? value : 1,
    }));
  };

  const handleSubmissionStatusChange = useCallback((value) => {
    setActiveSubmissionStatus(value);
  }, []);

  const handleRetryFetch = useCallback(() => {
    fetchResponseList(query);
  }, [fetchResponseList, query]);

  const handlePageChange = useCallback(
    (event, newPage) => {
      handleFilterChange('pageNumber', newPage + 1);
    },
    [handleFilterChange]
  );

  const handleRowsPerPageChange = useCallback(
    (event) => {
      updateQuery((previousQuery) => ({
        ...previousQuery,
        pageSize: Number(event.target.value),
        pageNumber: 1,
      }));
    },
    [updateQuery]
  );

  const handleResetFilters = () => {
    setSearchInput('');
    setQuery(getDefaultQuery(enforcedCompanyId));
  };

  const handleExportCurrentPage = () => {
    if (!visibleRows.length) {
      return;
    }

    const firstQuestionHeader = questionColumns[0]?.questionText || 'Question 1';
    const secondQuestionHeader = questionColumns[1]?.questionText || 'Question 2';

    const exportRows = visibleRows.map((response) => ({
      Employee: response?.user?.userName || 'Unknown employee',
      'Submitted At': response?.submissionStatus === 'not_filled' ? 'N/A' : formatDateTimeLabel(response?.submittedAt),
      'Survey Date': formatDateLabel(response?.surveyDate),
      [firstQuestionHeader]: questionColumns[0] ? getAnswerPreview(response, questionColumns[0].id) : 'N/A',
      [secondQuestionHeader]: questionColumns[1] ? getAnswerPreview(response, questionColumns[1].id) : 'N/A',
    }));

    const csv = generateCsv(csvConfig)(exportRows);
    download(csvConfig)(csv);
  };

  return (
    <>
      <Helmet>
        <title>Survey Responses</title>
      </Helmet>

      <BreadCrumb breadcrumbs={breadcrumbs} />

      <Container maxWidth="xl" sx={{ pb: 4,pt: 2 }}>
        <SurveyFilters
          searchInput={searchInput}
          query={query}
          onSearchChange={setSearchInput}
          onFilterChange={handleFilterChange}
          onResetFilters={handleResetFilters}
          roleOptions={roleOptions}
          companyOptions={companyOptions}
          canFilterCompany={canFilterCompany}
          activeFilterCount={activeFilterCount}
          activeSubmissionStatus={activeSubmissionStatus}
          filledCount={totalCount}
          notFilledCount={notFilledTotalCount}
          onSubmissionStatusChange={handleSubmissionStatusChange}
          onExportCurrentPage={handleExportCurrentPage}
          exportDisabled={!visibleRows.length}
        />

        <Card
          sx={{
            border: (theme) => `1px solid ${theme.palette.divider}`,
            overflow: 'hidden',
          }}
        >
          {(isRefetching || isMetaLoading) && <LinearProgress />}

          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 2.25,
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              backgroundColor: '#FCFCFD',
            }}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
            >
              <Box>
                <Typography variant="h6">Submission list</Typography>
                <Typography variant="body2" color="text.secondary">
                  {visibleCount
                    ? `Showing ${pageStart}-${pageEnd} of ${visibleCount} ${activeSubmissionStatus === 'filled' ? 'filled' : 'not-filled'} records`
                    : `No ${activeSubmissionStatus === 'filled' ? 'filled' : 'not-filled'} records matched the current filters`}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={`Page ${query.pageNumber} of ${totalPages}`} variant="outlined" size="small" />
                <Chip label={`Sorted by ${SORT_OPTIONS.find((option) => option.value === query.orderBy)?.label}`} size="small" />
                {currentUser?.companyId && !query.companyId && !canFilterCompany && (
                  <Chip label="Company scoped" variant="outlined" size="small" />
                )}
              </Stack>
            </Stack>
          </Box>

          <Box sx={{ p: { xs: 2, md: 3 } }}>
            {errorMessage ? (
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={handleRetryFetch}>
                    Retry
                  </Button>
                }
              >
                {errorMessage}
              </Alert>
            ) : null}

            {!isInitialLoading && !visibleRows.length ? (
              <EmptyContent
                filled
                title={activeSubmissionStatus === 'filled' ? 'No survey submissions found' : 'No not-filled forms found'}
                description={
                  activeSubmissionStatus === 'filled'
                    ? 'Try clearing a few filters or changing the search term to widen the results.'
                    : 'No pending survey forms matched the current filters.'
                }
                sx={{ py: 10 }}
              />
            ) : (
              <TableWrapper>
                <MaterialReactTable table={table} />
              </TableWrapper>
            )}
          </Box>

          <Divider />

          <TablePaginationCustom
            count={visibleCount}
            page={Math.max(query.pageNumber - 1, 0)}
            rowsPerPage={query.pageSize}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </Card>
      </Container>
    </>
  );
}

export default SurveyResponses;
